import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleRegisterClick = () => {
    if (user && user._id) {
      if (user.is_registered && user.is_paid) {
        navigate('/dashboard');
      } else if (user.is_registered && !user.is_paid) {
        navigate('/payment');
      } else {
        navigate('/register-details');
      }
    } else {
      navigate('/register');
    }
  };

  const [showToast, setShowToast] = useState('');

  const handleSearchClick = () => {
    navigate('/search');
  };

  const handleReferClick = () => {
    if (!user || !user._id) {
      setShowToast('Please register first to refer & earn');
      setTimeout(() => setShowToast(''), 3000);
    } else if (!user.is_registered || !user.is_paid) {
      setShowToast('Please complete registration and payment first');
      setTimeout(() => setShowToast(''), 3000);
    } else {
      navigate('/dashboard#referral');
    }
  };

  const handleWithdrawClick = () => {
    if (!user || !user._id) {
      setShowToast('Please register first to withdraw balance');
      setTimeout(() => setShowToast(''), 3000);
    } else if (!user.is_registered || !user.is_paid) {
      setShowToast('Please complete registration and payment first');
      setTimeout(() => setShowToast(''), 3000);
    } else {
      navigate('/dashboard#wallet');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <div
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1647108188039-4d5048f19a58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwZWxlZ2FudCUyMGxpcXVpZCUyMGdsYXNzJTIwYmFja2dyb3VuZCUyME9SJTIwcHJlbWl1bSUyMGFic3RyYWN0JTIwYmxhY2slMjAzZHxlbnwwfHx8fDE3NzU0NDk0NDN8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center"
          >
            <motion.img
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              src="https://customer-assets.emergentagent.com/job_donor-connect-hub-2/artifacts/urb11myp_cf61f971-770e-4673-bd11-3305f0999f09.png"
              alt="MyGaadiNumber"
              className="h-32 md:h-40 lg:h-48 mb-8 drop-shadow-2xl"
            />
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-none font-medium mb-6">
              Blood Group Emergency
              <br />
              <span className="text-[#FF3B30]">Access Platform</span>
            </h1>
            <p className="text-base md:text-lg text-[#A1A1A6] max-w-2xl mx-auto mb-12 leading-relaxed font-light">
              Register your blood group and vehicle details to help others in emergencies.
              Search by vehicle number to find blood group information when it matters most.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleRegisterClick}
                data-testid="register-cta-button"
                className="relative px-8 py-4 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full text-lg font-medium transition-all shadow-lg shadow-[#FF3B30]/40 overflow-hidden cta-premium-glow"
              >
                <span className="cta-shimmer" />
                Register / Consumer
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSearchClick}
                data-testid="search-cta-button"
                className="relative px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-xl rounded-full text-lg font-medium transition-all border border-white/30 overflow-hidden cta-premium-glow cta-search-glow"
              >
                <span className="cta-shimmer" />
                Search Blood Group (Using Vehicle Number)
              </motion.button>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleReferClick}
                data-testid="refer-earn-cta-button"
                className="px-8 py-4 bg-[#32D74B] hover:bg-[#2AB840] rounded-full text-lg font-medium transition-all text-black shadow-lg shadow-[#32D74B]/30"
              >
                Refer & Earn ₹50
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleWithdrawClick}
                data-testid="withdraw-cta-button"
                className="px-8 py-4 bg-[#FF9F0A] hover:bg-[#E89000] rounded-full text-lg font-medium transition-all text-black shadow-lg shadow-[#FF9F0A]/30"
              >
                Withdraw Balance
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            <div className="bg-[#121214] border border-white/10 rounded-3xl p-8 hover:-translate-y-1 transition-transform duration-300">
              <div className="text-4xl mb-4">🩸</div>
              <h3 className="text-xl font-medium mb-3">Quick Registration</h3>
              <p className="text-[#A1A1A6] text-sm">
                Register your blood group and vehicle details in minutes. Lifetime subscription for just ₹150.
              </p>
            </div>

            <div className="bg-[#121214] border border-white/10 rounded-3xl p-8 hover:-translate-y-1 transition-transform duration-300">
              <div className="text-4xl mb-4">🚗</div>
              <h3 className="text-xl font-medium mb-3">Vehicle Search</h3>
              <p className="text-[#A1A1A6] text-sm">
                Search by vehicle number to instantly find blood group information in emergency situations.
              </p>
            </div>

            <div className="bg-[#121214] border border-white/10 rounded-3xl p-8 hover:-translate-y-1 transition-transform duration-300">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-medium mb-3">Referral Rewards</h3>
              <p className="text-[#A1A1A6] text-sm">
                Earn ₹50 cashback for every referral. Get ₹25 discount when you use a referral code.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="bg-black py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-medium mb-4">How It Works</h2>
            <p className="text-[#A1A1A6] mb-12 max-w-2xl mx-auto">
              A simple 3-step process connecting vehicle owners with life-saving information
            </p>
            
            <img
              src="https://customer-assets.emergentagent.com/job_donor-connect-hub-2/artifacts/yfd7dm93_85a82409-e15e-434d-89c6-596a41477859.png"
              alt="How MyGaadiNumber Works"
              className="w-full max-w-2xl mx-auto rounded-2xl"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="text-5xl mb-4">🏍️</div>
                <h3 className="text-xl font-medium mb-2">Register Vehicle</h3>
                <p className="text-[#A1A1A6] text-sm">
                  Link your vehicle number with your blood group and family details
                </p>
              </div>

              <div className="text-center">
                <div className="text-5xl mb-4">👤</div>
                <h3 className="text-xl font-medium mb-2">Emergency Search</h3>
                <p className="text-[#A1A1A6] text-sm">
                  Anyone can search by vehicle number to find blood group in emergencies
                </p>
              </div>

              <div className="text-center">
                <div className="text-5xl mb-4">🩸</div>
                <h3 className="text-xl font-medium mb-2">Save Lives</h3>
                <p className="text-[#A1A1A6] text-sm">
                  Instant access to critical blood group information when every second counts
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {showToast && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#FF453A] text-white px-8 py-4 rounded-full font-medium shadow-lg shadow-[#FF453A]/40"
          data-testid="home-toast-message"
        >
          {showToast}
        </motion.div>
      )}
    </div>
  );
};

export default Home;
